import { useState, useCallback, useRef } from "react";
import { saveSchedulerTemplate } from "repositories/scheduler-repository";
import { handleError, handleSuccess } from "utils";
import {
  buildSaveSchedulerTemplatePayload,
  SCHEDULER_TEMPLATE_NAME_MAX_LENGTH,
} from "./eventsWorkspaceSaveTemplate.utils";
import { resolveDeleteRangeIsShift } from "./eventsWorkspaceClear.utils";

/**
 * Save Day/Week/Month as Template for Event Workspace.
 * Uses currently visible filtered records (via applied UserIds/OfficeIds/IsShift)
 * within the selected period. Guards against rapid multi-submit.
 * @param {{
 *   t: Function,
 *   calendarViewId: string,
 *   currentDate: Date,
 *   ownerId: number|null|undefined,
 *   visibleItems?: Array<{ id: number, isShift?: boolean }>,
 *   eventShiftFilter?: string[],
 *   officeIds?: number[],
 *   userIds?: number[],
 * }} options
 */
export function useEventsWorkspaceSaveTemplate({
  t,
  calendarViewId,
  currentDate,
  ownerId,
  visibleItems = [],
  eventShiftFilter = [],
  officeIds = [],
  userIds = [],
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modalIsShift, setModalIsShift] = useState(null);
  const isSavingRef = useRef(false);

  const canSaveAsTemplate =
    Boolean(ownerId) && visibleItems.length > 0 && !isSaving;

  const closeModal = useCallback(() => {
    if (isSavingRef.current) return;
    setIsModalOpen(false);
  }, []);

  const openSaveTemplateModal = useCallback(() => {
    if (!ownerId || isSavingRef.current) return;
    if (!visibleItems.length) {
      handleError(t("scheduler.saveTemplateEmpty"));
      return;
    }
    setModalIsShift(
      resolveDeleteRangeIsShift({
        clearableItems: visibleItems,
        eventShiftFilter,
      }),
    );
    setIsModalOpen(true);
  }, [ownerId, visibleItems, eventShiftFilter, t]);

  const executeSave = useCallback(
    async (rawName) => {
      if (!ownerId || isSavingRef.current || !isModalOpen) return false;

      const name = String(rawName || "").trim();
      if (!name) {
        handleError(
          t("form.errors.emptyField", {
            field: t("scheduler.templateName").toLowerCase(),
          }),
        );
        return false;
      }
      if (name.length > SCHEDULER_TEMPLATE_NAME_MAX_LENGTH) {
        handleError(t("scheduler.templateNameMaxLength"));
        return false;
      }

      isSavingRef.current = true;
      setIsSaving(true);
      try {
        const payload = buildSaveSchedulerTemplatePayload({
          ownerId,
          name,
          calendarViewId,
          currentDate,
          visibleItems,
          eventShiftFilter,
          officeIds,
          userIds,
        });
        // Prefer IsShift snapshotted when the modal opened (matches visible filters).
        payload.IsShift = modalIsShift;
        await saveSchedulerTemplate(payload);
        handleSuccess(t("scheduler.saveTemplateSuccess"));
        setIsModalOpen(false);
        return true;
      } catch (error) {
        handleError(error?.message || error);
        return false;
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    },
    [
      ownerId,
      isModalOpen,
      calendarViewId,
      currentDate,
      visibleItems,
      eventShiftFilter,
      officeIds,
      userIds,
      modalIsShift,
      t,
    ],
  );

  return {
    isModalOpen,
    isSaving,
    canSaveAsTemplate,
    openSaveTemplateModal,
    closeModal,
    executeSave,
  };
}
